/**
 * A shared room screen, fitted flush onto a venue model's screen surface.
 * Shows (in order): the room's cloud browser (Hyperbeam), a synced direct
 * video, the room's LiveKit screen share, or a quiet hint. Everything renders
 * as three.js textures, so it works the same on desktop and in a headset.
 *
 * Input: desktop click/scroll and VR controller rays on the screen are sent to
 * the cloud browser as pointer/wheel events; after a desktop click, typing
 * goes to the screen until Esc or a click elsewhere.
 *
 * Cost: a viewer only connects to the cloud browser within ~15 m of the
 * screen and disconnects after 20 s beyond ~20 m (see screenRangeStep);
 * otherwise the screen shows "Walk closer to watch".
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Text } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useMediaXRSession } from '../hooks/useMediaXRSession';
import { useHyperbeamScreen } from '../hooks/useHyperbeamScreen';
import { useSyncedVideo } from '../hooks/useSyncedVideo';
import { useWorldStore, type ScreenPropItem } from '../stores/worldStore';
import { useRoomScreenStore, useScreenLinkLocked } from '../services/roomScreens';
import { screenRange, screenRangeStep, type RangeState } from '../utils/roomScreenRange';
import { canCaptureScreen } from '../services/screenShareService';
import { reportScreenDistance } from './RoomScreenDock';
import './MediaPanels.css';

const noRaycast = () => null;

/** Live LiveKit screen share → texture (the screen's original job). */
function useStreamTexture(stream: MediaStream | null) {
  const [state, setState] = useState<{ tex: THREE.VideoTexture; aspect: number } | null>(null);
  useEffect(() => {
    if (!stream) return;
    const video = document.createElement('video');
    video.muted = true; video.playsInline = true; video.autoplay = true;
    video.srcObject = stream;
    const tex = new THREE.VideoTexture(video);
    tex.colorSpace = THREE.SRGBColorSpace;
    let disposed = false;
    const ready = () => { if (!disposed && video.videoWidth) setState({ tex, aspect: video.videoWidth / video.videoHeight }); };
    video.addEventListener('playing', ready);
    void video.play().catch(() => {});
    return () => { disposed = true; video.pause(); video.srcObject = null; tex.dispose(); setState(null); };
  }, [stream]);
  return state;
}

function hostOf(url: string) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

// Desktop keyboard focus: one screen at a time receives typing.
let focusedScreen: string | null = null;
const focusListeners = new Set<() => void>();
function setFocusedScreen(id: string | null) { focusedScreen = id; focusListeners.forEach((l) => l()); }

export default function RoomScreen({ id, item, isOwner = false }: { id: string; item: ScreenPropItem; isOwner?: boolean }) {
  const inVR = !!useMediaXRSession();
  const w = item.width ?? 4;
  const h = item.height ?? 2.25;
  const screen = useRoomScreenStore((s) => s.screens[id]);
  const notice = useRoomScreenStore((s) => s.notice[id]);
  const busy = useRoomScreenStore((s) => !!s.busy[id]);
  const liveShare = useWorldStore((s) => s.activeScreenShare);

  const member = !useScreenLinkLocked();
  // In range of the screen? Decided in useFrame below; the screen you just
  // shared to always connects (so the sharer can navigate it).
  const [inRange, setInRange] = useState(false);
  const rangeRef = useRef<RangeState>({ inRange: false, farSince: null });
  const browserOn = screen?.mode === 'browser';
  const hb = useHyperbeamScreen(id, browserOn ? screen.embedUrl : null, browserOn ? screen.sessionId : null, inRange || busy);
  const tooFar = browserOn && !inRange && !busy;
  const video = useSyncedVideo(screen?.mode === 'video' ? screen.url : null, screen?.mode === 'video' ? screen.video : null);
  const stream = useStreamTexture(!screen ? liveShare : null);

  const source = screen?.mode === 'browser' ? (hb.texture ? { tex: hb.texture, aspect: hb.aspect || w / h } : null)
    : screen?.mode === 'video' ? (video.texture ? { tex: video.texture, aspect: video.aspect || 16 / 9 } : null)
    : stream ? { tex: stream.tex, aspect: stream.aspect } : null;

  // Fit inside the surface (letterbox), except the cloud browser, which is
  // already rendered at the screen's own aspect ratio.
  const [pw, ph] = useMemo(() => {
    if (!source) return [w, h];
    const a = source.aspect;
    return a > w / h ? [w, w / a] : [h * a, h];
  }, [source?.aspect, w, h]);

  const [focused, setFocused] = useState(false);
  useEffect(() => {
    const l = () => setFocused(focusedScreen === id);
    focusListeners.add(l);
    return () => { focusListeners.delete(l); if (focusedScreen === id) setFocusedScreen(null); };
  }, [id]);

  // Forward typing to the cloud browser while this screen has focus.
  useEffect(() => {
    if (!focused) return;
    const isField = (t: EventTarget | null) => t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    const onKey = (e: KeyboardEvent) => {
      if (isField(e.target)) return;
      if (e.key === 'Escape') { setFocusedScreen(null); return; }
      const client = hb.hbRef.current;
      if (!client) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      client.sendEvent({ type: e.type as 'keydown' | 'keyup', key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey });
    };
    const blur = () => { if (!(window as any).__roomScreenClick) setFocusedScreen(null); };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('keyup', onKey, true);
    window.addEventListener('pointerdown', blur);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('keyup', onKey, true);
      window.removeEventListener('pointerdown', blur);
    };
  }, [focused, hb.hbRef]);

  const lastMove = useRef(0);
  const toBrowser = (e: ThreeEvent<PointerEvent | WheelEvent>) => {
    const uv = e.uv;
    if (!uv) return null;
    return { x: Math.min(1, Math.max(0, uv.x)), y: Math.min(1, Math.max(0, 1 - uv.y)) };
  };
  const interactive = screen?.mode === 'browser' && !!hb.texture;
  const handlers = {
    onPointerMove: (e: ThreeEvent<PointerEvent>) => {
      if (!interactive) return;
      const now = performance.now();
      if (now - lastMove.current < 33) return;
      lastMove.current = now;
      const p = toBrowser(e);
      if (p) hb.hbRef.current?.sendEvent({ type: 'mousemove', ...p });
    },
    onPointerDown: (e: ThreeEvent<PointerEvent>) => {
      if (!screen) return;
      e.stopPropagation();
      if (screen.mode === 'video' && video.needsSound) { video.enableSound(); return; }
      if (!interactive) return;
      const p = toBrowser(e);
      if (!p) return;
      hb.hbRef.current?.sendEvent({ type: 'mousemove', ...p });
      hb.hbRef.current?.sendEvent({ type: 'mousedown', ...p, button: e.button === 2 ? 2 : 0 });
      if (!inVR) {
        (window as any).__roomScreenClick = true;
        setTimeout(() => { (window as any).__roomScreenClick = false; }, 0);
        setFocusedScreen(id);
      }
    },
    onPointerUp: (e: ThreeEvent<PointerEvent>) => {
      if (!interactive) return;
      e.stopPropagation();
      const p = toBrowser(e);
      if (p) hb.hbRef.current?.sendEvent({ type: 'mouseup', ...p, button: e.button === 2 ? 2 : 0 });
    },
    onWheel: (e: ThreeEvent<WheelEvent>) => {
      if (!interactive) return;
      e.stopPropagation();
      hb.hbRef.current?.sendEvent({ type: 'wheel', deltaY: e.nativeEvent.deltaY });
    },
    onClick: (e: ThreeEvent<MouseEvent>) => { if (screen) e.stopPropagation(); },
  };

  const status = notice || (busy ? 'Starting…' : '')
    || (tooFar ? 'Walk closer to watch' : '')
    || (screen?.mode === 'browser' ? (
      hb.status === 'connecting' || (hb.status === 'idle' && !hb.texture) ? 'Connecting to the cloud browser…'
        : hb.status === 'reconnecting' ? 'Reconnecting…'
          : hb.status === 'ended' ? 'The cloud browser ended. Share a link to start again.'
            : hb.status === 'error' ? 'Could not connect to the cloud browser' : '') : '')
    || (screen?.mode === 'video' ? video.status : '');
  const hint = !screen && !stream
    ? (inVR ? (member ? 'Share a link to this screen from your menu (A / X or wrist)' : '')
      : member ? (canCaptureScreen() ? 'Share a link or your screen from the bar below' : 'Share a link to this screen from the bar below')
        : canCaptureScreen() ? 'Share your screen from the bar below' : '')
    : '';
  const caption = screen ? `${screen.mode === 'video' ? 'Video' : 'Browser'} · ${hostOf(screen.url)}${screen.startedByName ? ` · shared by ${screen.startedByName}` : ''}` : '';
  // Desktop controls live in the bottom dock (RoomScreenDock): report how far
  // this screen is so the dock follows the nearest one.
  const root = useRef<THREE.Group>(null);
  const tick = useRef(0);
  const wp = useMemo(() => new THREE.Vector3(), []);
  const cp = useMemo(() => new THREE.Vector3(), []);
  const range = useMemo(() => screenRange(w), [w]);
  useFrame(({ camera, gl }) => {
    if (++tick.current % 15 || !root.current) return;
    root.current.getWorldPosition(wp);
    // In a headset the viewer is wherever the XR camera is.
    const cam = gl.xr?.isPresenting ? gl.xr.getCamera() : camera;
    cam.getWorldPosition(cp);
    const d = wp.distanceTo(cp);
    const next = screenRangeStep(rangeRef.current, d, performance.now(), range);
    if (next.inRange !== rangeRef.current.inRange) setInRange(next.inRange);
    rangeRef.current = next;
    if (inVR) { reportScreenDistance(id, null); return; }
    reportScreenDistance(id, d, isOwner);
  });
  useEffect(() => () => reportScreenDistance(id, null), [id]);
  const fs = Math.min(w, h * 1.6) * 0.03;

  return <group ref={root}>
    {/* Black backing so letterboxed video reads as a screen, not a hole. */}
    {(source || screen) && <mesh position={[0, 0, -0.001]} raycast={noRaycast}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial color="#000000" toneMapped={false} polygonOffset polygonOffsetFactor={-1} />
    </mesh>}
    {source && <mesh position={[0, 0, 0.0005]} raycast={noRaycast}>
      <planeGeometry args={[pw, ph]} />
      <meshBasicMaterial key={source.tex.uuid} map={source.tex} toneMapped={false} polygonOffset polygonOffsetFactor={-2} />
    </mesh>}
    {/* Hit surface for clicks, scroll and VR rays (UVs span the whole screen). */}
    <mesh name="room-screen-surface" userData={{ roomScreenId: id }} position={[0, 0, 0.001]} {...handlers}>
      <planeGeometry args={[w, h]} />
      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
    </mesh>
    {status && <group position={[0, 0, 0.01]}>
      <mesh raycast={noRaycast}><planeGeometry args={[Math.min(w * 0.8, status.length * fs * 0.55 + fs * 2), fs * 2.4]} /><meshBasicMaterial color="#0b141e" transparent opacity={0.82} toneMapped={false} /></mesh>
      <Text raycast={noRaycast} position={[0, 0, 0.002]} fontSize={fs} maxWidth={w * 0.76} textAlign="center" color="#e6eef6">{status}</Text>
    </group>}
    {hint && <group position={[0, -h / 2 + fs * 1.9, 0.01]}>
      <mesh raycast={noRaycast}><planeGeometry args={[Math.min(w * 0.9, hint.length * fs * 0.42 + fs * 1.6), fs * 1.9]} /><meshBasicMaterial color="#0b141e" transparent opacity={0.72} toneMapped={false} /></mesh>
      <Text raycast={noRaycast} position={[0, 0, 0.002]} fontSize={fs * 0.75} maxWidth={w * 0.86} textAlign="center" color="#bdf3ec">{hint}</Text>
    </group>}
    {(hb.warning || video.needsSound || focused) && <Text raycast={noRaycast} position={[0, h / 2 - fs * 1.4, 0.01]} fontSize={fs * 0.8} color="#ffd18a" outlineWidth={fs * 0.06} outlineColor="#000">
      {hb.warning || (video.needsSound ? 'Click the screen for sound' : 'Typing goes to the screen · Esc to stop')}
    </Text>}
    {caption && inVR && <Text raycast={noRaycast} position={[-w / 2, -h / 2 - fs * 1.1, 0.01]} anchorX="left" fontSize={fs * 0.7} color="#a3b4c4">{caption}</Text>}
  </group>;
}
